import { __assign, __read, __rest } from "tslib";
import React, { memo, useContext, useState } from 'react';
import { DatePicker } from '@mercell/date-picker-react';
import FieldError from '@mercell/field-error-react';
import { utcToZonedTime, format } from 'date-fns-tz';
import { Field } from 'react-final-form';
import stringify from 'fast-safe-stringify';
import { addDays, formatISO, parseISO } from 'date-fns';
import { FormContext } from '../utils/FormContext';
import { prepareAndToggleFieldErrors } from '../utils/prepareAndToggleFieldErrors';
import { resolveUseSamePath } from '../utils/resolveUseSamePath';
import { nestedResolveUseSamePath } from '../utils/nestedResolveUseSamePath';
import { nl2br } from '../utils/nl2br';
function isoOffsetToMinutes(isoOffset) {
    var offsetPattern = /^([+-])(\d{2}):(\d{2})$/;
    var match = isoOffset.match(offsetPattern);
    if (match) {
        var sign = match[1] === '+' ? -1 : 1;
        var hours = parseInt(match[2], 10);
        var minutes = parseInt(match[3], 10);
        var totalMinutes = (hours * 60 + minutes) * sign;
        return totalMinutes;
    }
    return NaN;
}
function minutesToIsoOffset(offsetMinutes) {
    var offsetHours = Math.floor(offsetMinutes / 60);
    var offsetMinutesRemainder = Math.abs(offsetMinutes % 60);
    var sign = offsetHours >= 0 ? '+' : '-';
    var formattedHours = Math.abs(offsetHours).toString().padStart(2, '0');
    var formattedMinutes = offsetMinutesRemainder.toString().padStart(2, '0');
    return "".concat(sign).concat(formattedHours, ":").concat(formattedMinutes);
}
function isDateStringUtcFormat(input) {
    var isoFormatRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,3})?\+00:00$/;
    return isoFormatRegex.test(input);
}
export var DatePickerField = memo(function (_a) {
    var _b, _c, _d;
    var field = _a.field, name = _a.name;
    var _e = field, _f = _e.fieldWrapperProperties, fieldWrapperProperties = _f === void 0 ? {} : _f, _g = _e.fieldLabel, fieldLabel = _g === void 0 ? function () { return null; } : _g, _h = _e.ariaRequired, ariaRequired = _h === void 0 ? false : _h, showTimeSelect = _e.showTimeSelect, _j = _e.submitWithTimeZone, submitWithTimeZone = _j === void 0 ? false : _j, timeZone = _e.timeZone, showDateRangeWithTimeZone = _e.showDateRangeWithTimeZone, validations = _e.validations, id = _e.id, disabled = _e.disabled, locale = _e.locale, dateFormat = _e.dateFormat, startDateRangeFormat = _e.startDateRangeFormat, endDateRangeFormat = _e.endDateRangeFormat, placeholder = _e.placeholder, placeholderText = _e.placeholderText, associatedValidationBasedOnOtherFieldValues = _e.associatedValidationBasedOnOtherFieldValues, arrayOfFieldsToRevalidateOnChange = _e.arrayOfFieldsToRevalidateOnChange, _k = _e.partialFieldProps, partialFieldProps = _k === void 0 ? {} : _k, restProps = __rest(_e, ["fieldWrapperProperties", "fieldLabel", "ariaRequired", "showTimeSelect", "submitWithTimeZone", "timeZone", "showDateRangeWithTimeZone", "validations", "id", "disabled", "locale", "dateFormat", "startDateRangeFormat", "endDateRangeFormat", "placeholder", "placeholderText", "associatedValidationBasedOnOtherFieldValues", "arrayOfFieldsToRevalidateOnChange", "partialFieldProps"]);
    var _l = __read(useState(false), 2), reApplyRequiredIndicator = _l[0], setReApplyRequiredIndicator = _l[1];
    var _m = useContext(FormContext), validationCallback = _m.validationCallback, showErrorsOnInitialRender = _m.showErrorsOnInitialRender;
    var validateMethod = function () { return null; };
    var updatedConditions = nestedResolveUseSamePath((_c = (_b = associatedValidationBasedOnOtherFieldValues === null || associatedValidationBasedOnOtherFieldValues === void 0 ? void 0 : associatedValidationBasedOnOtherFieldValues.conditionalFields) === null || _b === void 0 ? void 0 : _b.conditions) !== null && _c !== void 0 ? _c : [], name);
    var updatedAssociatedValidationBasedOnOtherFieldValues = {
        logicalOperator: (_d = associatedValidationBasedOnOtherFieldValues === null || associatedValidationBasedOnOtherFieldValues === void 0 ? void 0 : associatedValidationBasedOnOtherFieldValues.conditionalFields) === null || _d === void 0 ? void 0 : _d.logicalOperator,
        conditions: updatedConditions,
    };
    if ((validations || associatedValidationBasedOnOtherFieldValues) &&
        validationCallback) {
        validateMethod = function (fieldValue, allValues) {
            return validationCallback(fieldValue, validations, allValues, {
                conditionalFields: updatedAssociatedValidationBasedOnOtherFieldValues,
                rules: associatedValidationBasedOnOtherFieldValues === null || associatedValidationBasedOnOtherFieldValues === void 0 ? void 0 : associatedValidationBasedOnOtherFieldValues.rules,
                message: associatedValidationBasedOnOtherFieldValues === null || associatedValidationBasedOnOtherFieldValues === void 0 ? void 0 : associatedValidationBasedOnOtherFieldValues.message,
                reApplyRequiredIndicator: associatedValidationBasedOnOtherFieldValues === null || associatedValidationBasedOnOtherFieldValues === void 0 ? void 0 : associatedValidationBasedOnOtherFieldValues.reApplyRequiredIndicator,
            }, setReApplyRequiredIndicator);
        };
    }
    var validateFields = arrayOfFieldsToRevalidateOnChange === null || arrayOfFieldsToRevalidateOnChange === void 0 ? void 0 : arrayOfFieldsToRevalidateOnChange.map(function (fieldToRevalidate) {
        if (fieldToRevalidate.useSamePath) {
            return resolveUseSamePath(name, fieldToRevalidate.fieldName);
        }
        return fieldToRevalidate.fieldName;
    });
    var _o = partialFieldProps.parse, parse = _o === void 0 ? function (val) { return val; } : _o;
    return (React.createElement(Field, __assign({}, partialFieldProps, { parse: parse, name: name, validate: validateMethod, validateFields: validateFields }), function (_a) {
        var input = _a.input, meta = _a.meta;
        var _b = prepareAndToggleFieldErrors(meta, showErrorsOnInitialRender), showErrors = _b.showErrors, errors = _b.errors;
        var value = input.value, restInputProps = __rest(input, ["value"]);
        var endDateRange;
        var parsedDate;
        var isValueUTC;
        if (value) {
            parsedDate = parseISO(value);
            isValueUTC = isDateStringUtcFormat(value);
            if (!showTimeSelect && showDateRangeWithTimeZone) {
                var offsetPattern = /([+-]\d{2}:\d{2})$/;
                var match = value.match(offsetPattern);
                if (match) {
                    var currentLocalDate = new Date();
                    var currentDateOffset = currentLocalDate.getTimezoneOffset();
                    var offset = match[1];
                    var offsetMinutes = isoOffsetToMinutes(offset);
                    if (offsetMinutes !== currentDateOffset) {
                        endDateRange = parseISO(value);
                        endDateRange = addDays(endDateRange, 1);
                    }
                }
            }
        }
        return (React.createElement("div", __assign({}, fieldWrapperProperties, { "data-field": name }),
            fieldLabel(id || name, reApplyRequiredIndicator),
            React.createElement(DatePicker, __assign({}, restInputProps, restProps, { id: id || name, locale: locale, name: name, showTimeSelect: showTimeSelect, startDateRangeFormat: startDateRangeFormat, endDateRangeFormat: endDateRangeFormat, endDateRange: endDateRange, dateFormat: dateFormat, "aria-required": ariaRequired, timeZone: timeZone, isValueUTC: isValueUTC, "aria-invalid": showErrors, invalid: showErrors, placeholderText: placeholder || placeholderText, selected: parsedDate, disabled: disabled, onChange: function (date) {
                    if (!timeZone &&
                        date &&
                        submitWithTimeZone) {
                        var isoDateString = formatISO(date);
                        input.onChange(isoDateString);
                    }
                    else if (date) {
                        var utcDateString = format(utcToZonedTime(date, 'UTC'), "yyyy-MM-dd'T'HH:mm:ssXXX").replace(/Z|[\+\-]\d\d:\d\d/g, '+00:00');
                        input.onChange(utcDateString);
                    }
                    else {
                        input.onChange('');
                    }
                } })),
            showErrors && (React.createElement(FieldError, null, nl2br(errors)))));
    }));
}, function (prevProps, nextProps) {
    try {
        return stringify(prevProps) === stringify(nextProps);
    }
    catch (e) {
        console.warn("DatePickerField: ".concat(e));
        return false;
    }
});
DatePickerField.displayName = 'DatePickerField';
